import { useEffect, useState } from "react";
import { getAuthHeaders } from "../utils/authHeaders";
import {
    getEnvFallbackPlatformFlags,
    parsePlatformFlagsResponse,
    PlatformFlagsMap,
    setRuntimePlatformFlags,
} from "../utils/featureFlags";

const PLATFORM_FLAGS_CACHE_TTL_MS = 2 * 60 * 1000;

interface PlatformFlagsCache {
    data: PlatformFlagsMap;
    timestamp: number;
}

let platformFlagsCache: PlatformFlagsCache | null = null;

function getCached(): PlatformFlagsMap | null {
    if (!platformFlagsCache) return null;
    if (Date.now() - platformFlagsCache.timestamp > PLATFORM_FLAGS_CACHE_TTL_MS) {
        platformFlagsCache = null;
        return null;
    }
    return platformFlagsCache.data;
}

function setCached(data: PlatformFlagsMap) {
    platformFlagsCache = { data, timestamp: Date.now() };
    setRuntimePlatformFlags(data);
}

export interface UsePlatformFeatureFlagsResult {
    flags: PlatformFlagsMap;
    loading: boolean;
    error: boolean;
    isSmsLoginEnabled: boolean;
    isBullionEnvEnabled: boolean;
    isSixesEnvEnabled: boolean;
    isNewRegistrationBonusEnabled: boolean;
    isLeaguesEnvEnabled: boolean;
}

/**
 * Shared hook for `/platform-flags`. Caches for 2 minutes so login, layout, and routes
 * reuse the same runtime admin overrides without a frontend redeploy.
 */
export function usePlatformFeatureFlags(): UsePlatformFeatureFlagsResult {
    const envFallback = getEnvFallbackPlatformFlags();
    const cached = getCached();
    const [flags, setFlags] = useState<PlatformFlagsMap>(cached ?? envFallback);
    const [loading, setLoading] = useState(!cached);
    const [error, setError] = useState(false);

    useEffect(() => {
        const cachedNow = getCached();
        if (cachedNow) {
            setFlags(cachedNow);
            setLoading(false);
            setError(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(false);
        fetch(`${process.env.API_URL}/platform-flags`, {
            method: "GET",
            headers: getAuthHeaders(),
        })
            .then((res) => {
                if (cancelled) return null;
                if (res.status !== 200) return Promise.reject(new Error(`HTTP ${res.status}`));
                return res.json();
            })
            .then((parsed) => {
                if (cancelled) return;
                const parsedFlags = parsePlatformFlagsResponse(parsed);
                if (!parsedFlags) {
                    setError(true);
                    return;
                }
                setFlags(parsedFlags);
                setCached(parsedFlags);
                setError(false);
            })
            .catch(() => {
                if (!cancelled) setError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return {
        flags,
        loading,
        error,
        isSmsLoginEnabled: flags.sms_login,
        isBullionEnvEnabled: flags.bullion_env,
        isSixesEnvEnabled: flags.sixes_env,
        isNewRegistrationBonusEnabled: flags.new_registration_bonus,
        isLeaguesEnvEnabled: flags.leagues_env,
    };
}
